// =============================================================================
// effect-kind-resolver-registry.ts — pure-function port of Cards/EffectKindResolverRegistry.cs
//
// Switch dispatch over 17 effect kinds:
//   • Active 6: TokenDelta, PollutionDelta, EnergyDelta, StealEnergy, AuraApply, BuildCostMod
//   • Conditional: NOT registered — handled upstream in EffectPipeline via predicateGate
//   • Deferred 9 (WEB.3): Decommission, Destroy, StealCard, StealFacility, DiceOutcome,
//     Immunity, CancelDisaster, FacilityTransfer, RngBranch → EffectResult.Skipped
//   • TollMod: flows through AuraApply (auraScope=Toll) — no separate resolver
//
// Returns null when no resolver registered (pipeline returns Skipped).
// Pure-function discipline: NO module state, NO singleton. State threaded via PipelineServices.
// =============================================================================

import type { CardDef, CardEffect, CardEffectKind } from "@gdg/shared";
import type { EffectContext } from "../card-effect-context.js";
import { EffectResult } from "../effect-result.js";
import type { ActionLog, JsonValue } from "../rng.js";
import { TRIGGER_PERMANENT_CHARGES, registerTrigger } from "../services/trigger-service.js";
import type { TriggerInstanceInput } from "../services/trigger-service.js";
import type { PipelineServices } from "./pipeline-services.js";

// ---------------------------------------------------------------------------
// ResolverResult — per-resolver outcome
// ---------------------------------------------------------------------------

export interface ResolverResult {
  readonly result: EffectResult;
  readonly log: ActionLog;
  readonly services: PipelineServices;
}

// ---------------------------------------------------------------------------
// Case-insensitive enum parse (Unity parity: Enum.TryParse(..., true, ...)).
// EffectKindResolverRegistry.cs:247 / :314 / :316 all pass ignoreCase=true.
// Shared with build-cost-service.ts via services/_facility-enum.ts.
// ---------------------------------------------------------------------------

import {
  FACILITY_CATEGORY_VALUES,
  FACILITY_TYPE_VALUES,
  tryParseEnumIgnoreCase,
} from "../services/_facility-enum.js";
import { getCategory } from "../services/facility-classifier.js";

// Cluster F category/type filter check. Returns:
//   { allowed: true } if effect has no filter or facility matches.
//   { allowed: false, reason: ... } if mismatch — caller logs Skipped.
// Filter token may be FacilityCategory ("Fossil") OR FacilityType ("Coal"),
// case-insensitive (Unity Enum.TryParse(..., true) parity).
function checkCategoryFilter(
  effect: CardEffect,
  facilityType: FacilityType,
): { allowed: true } | { allowed: false; reason: string } {
  const filters = effect.categoryFilter;
  if (filters === undefined || filters.length === 0) return { allowed: true };
  const raw = filters[0];
  if (raw === undefined || raw.length === 0) return { allowed: true };

  const parsedCat = tryParseEnumIgnoreCase(raw, FACILITY_CATEGORY_VALUES);
  if (parsedCat !== null) {
    return getCategory(facilityType) === parsedCat
      ? { allowed: true }
      : { allowed: false, reason: `category-mismatch:${parsedCat}-vs-${facilityType}` };
  }

  const parsedFt = tryParseEnumIgnoreCase(raw, FACILITY_TYPE_VALUES);
  if (parsedFt !== null) {
    return facilityType === parsedFt
      ? { allowed: true }
      : { allowed: false, reason: `type-mismatch:${parsedFt}-vs-${facilityType}` };
  }

  return { allowed: false, reason: `unknown-filter:${raw}` };
}

// ---------------------------------------------------------------------------
// resolveSeat — port of ResolveSeat (private in EffectKindResolverRegistry.cs)
// ---------------------------------------------------------------------------

function resolveSeat(target: string, ctx: Readonly<EffectContext>): number {
  switch (target) {
    case "Self":
    case "Drawer":
      return ctx.sourceSeat;
    case "OpponentChosen":
    case "OpponentRandom":
      return ctx.victimSeat;
    case "Global":
      return -1;
    default:
      console.error(
        `[Resolver] Unsupported target ${target} (Phase 3.1b only handles Self/Drawer/OpponentChosen/Global).`,
      );
      return -1;
  }
}

// ---------------------------------------------------------------------------
// Active resolvers (6)
// ---------------------------------------------------------------------------

import type { FacilityCategory, FacilityType } from "@gdg/shared";
import { register as registerAura } from "../services/aura-manager.js";
import type { AuraInstance } from "../services/aura-manager.js";
import { tryGet } from "../services/card-instance-service.js";
import { addEnergy, getEnergyCount, resolveDeficit } from "../services/energy-service.js";
import {
  FacilityOwnershipError,
  getByTile,
  removeFacility,
  transferFacility,
} from "../services/facility-ownership-service.js";
import { consumeIfMatches } from "../services/immunity-service.js";
import { enqueue, tryConsume } from "../services/pending-effect-queue.js";
import type { PendingEffect } from "../services/pending-effect-queue.js";
import { addPollution } from "../services/pollution-service.js";
import { addTokens, getTokenBalance, trySpend } from "../services/token-service.js";

function resolveTokenDelta(
  e: CardEffect,
  ctx: Readonly<EffectContext>,
  services: PipelineServices,
  log: ActionLog,
  scaledDelta: number,
): ResolverResult {
  const seat = resolveSeat(e.target, ctx);
  if (seat < 0) {
    console.error(`[Resolver:TokenDelta] No valid seat for target=${e.target}`);
    const nextLog = log.append({
      kind: "resolver.failed",
      data: { kind: "TokenDelta", reason: "no-valid-seat", target: e.target } as JsonValue,
    });
    return { result: EffectResult.Failed, log: nextLog, services };
  }

  // WEB.4.D — HalveNextTokenLoss: Climate_Adaptation_Strategy registers a one-charge
  // PEQ entry. When the next negative TokenDelta fires, consume the entry and halve
  // the loss (rounded toward zero — ceiling of the absolute value gives a smaller loss).
  // Consumed BEFORE applying the delta so the adjusted value is what the seat receives.
  // Guard: only checks PEQ for negative deltas (token gains are unaffected).
  let effectiveDelta = scaledDelta;
  let currentServices = services;
  let currentLog = log;
  if (scaledDelta < 0) {
    const consumeResult = tryConsume(
      currentServices.peq,
      currentLog,
      seat,
      "HalveNextTokenLoss",
      null,
      null,
    );
    if (consumeResult.ok) {
      // Halve the loss: ceil(|delta| / 2) → reduce magnitude.
      // e.g. -10 → -5, -7 → -4, -1 → -1 (minimum 1 loss when charge consumed)
      const absHalved = Math.ceil(Math.abs(scaledDelta) / 2);
      effectiveDelta = -absHalved;
      currentServices = { ...currentServices, peq: consumeResult.newState };
      currentLog = consumeResult.log.append({
        kind: "resolver.applied",
        data: {
          kind: "HalveNextTokenLoss",
          seat,
          originalDelta: scaledDelta,
          effectiveDelta,
        } as JsonValue,
      });
    }
  }

  const srcLabel = `Card:${ctx.cardTags.length > 0 ? ctx.cardTags[0] : "?"}`;
  const { newState, log: addLog } = addTokens(
    currentServices.token,
    currentLog,
    seat,
    effectiveDelta,
    srcLabel,
  );
  return {
    result: EffectResult.Applied,
    log: addLog,
    services: { ...currentServices, token: newState },
  };
}

function resolvePollutionDelta(
  _e: CardEffect,
  ctx: Readonly<EffectContext>,
  services: PipelineServices,
  log: ActionLog,
  scaledDelta: number,
): ResolverResult {
  const srcLabel = `Card:${ctx.cardTags.length > 0 ? ctx.cardTags[0] : "?"}`;
  const srcSeat: number | null = ctx.sourceSeat >= 0 ? ctx.sourceSeat : null;
  const { newState, log: addLog } = addPollution(
    services.pollution,
    log,
    scaledDelta,
    srcLabel,
    srcSeat,
  );
  return {
    result: EffectResult.Applied,
    log: addLog,
    services: { ...services, pollution: newState },
  };
}

function resolveEnergyDelta(
  e: CardEffect,
  ctx: Readonly<EffectContext>,
  services: PipelineServices,
  log: ActionLog,
  scaledDelta: number,
): ResolverResult {
  const seat = resolveSeat(e.target, ctx);
  if (seat < 0) {
    console.error(`[Resolver:EnergyDelta] No valid seat for target=${e.target}`);
    const nextLog = log.append({
      kind: "resolver.failed",
      data: { kind: "EnergyDelta", reason: "no-valid-seat", target: e.target } as JsonValue,
    });
    return { result: EffectResult.Failed, log: nextLog, services };
  }

  const srcLabel = `Card:${ctx.cardTags.length > 0 ? ctx.cardTags[0] : "?"}`;
  const prev = getEnergyCount(services.energy, seat);
  const { newState: energyAfterAdd, log: addLog } = addEnergy(
    services.energy,
    log,
    seat,
    scaledDelta,
    srcLabel,
  );

  let currentServices: PipelineServices = { ...services, energy: energyAfterAdd };
  let currentLog = addLog;

  // Deficit-pollution rule: ONLY for Self-target negative deltas.
  // Unity parity (EffectKindResolverRegistry.cs:171): drawer-target negative
  // deltas do NOT trigger deficit pollution; that path runs through
  // StealEnergy (separate resolver) which has its own deficit logic.
  if (e.target === "Self" && scaledDelta < 0) {
    const rawResult = prev + scaledDelta;
    const shortfall = rawResult < 0 ? -rawResult : 0;
    if (shortfall > 0) {
      const deficitResult = resolveDeficit(currentLog, seat, shortfall);
      currentLog = deficitResult.log;
      // Apply deficit pollution to global pollution state.
      const { newState: pollAfter, log: pollLog } = addPollution(
        currentServices.pollution,
        currentLog,
        deficitResult.pollutionDelta,
        "EnergyDeficit",
        seat,
      );
      currentLog = pollLog;
      currentServices = { ...currentServices, pollution: pollAfter };
    }
  }

  return { result: EffectResult.Applied, log: currentLog, services: currentServices };
}

function resolveStealEnergy(
  e: CardEffect,
  ctx: Readonly<EffectContext>,
  services: PipelineServices,
  log: ActionLog,
): ResolverResult {
  if (ctx.victimSeat === ctx.sourceSeat || ctx.victimSeat < 0) {
    console.error(
      `[Resolver:StealEnergy] victimSeat (${ctx.victimSeat}) invalid (source=${ctx.sourceSeat})`,
    );
    const nextLog = log.append({
      kind: "resolver.failed",
      data: { kind: "StealEnergy", reason: "invalid-victim-seat" } as JsonValue,
    });
    return { result: EffectResult.Failed, log: nextLog, services };
  }

  const requested = e.valueInt ?? 0;
  const victimHas = getEnergyCount(services.energy, ctx.victimSeat);
  const stolen = Math.min(requested, victimHas);

  if (stolen <= 0) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: { kind: "StealEnergy", reason: "VictimEmpty", victimSeat: ctx.victimSeat } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // No deficit/pollution side-effect from theft (locked rule).
  const { newState: afterVictim, log: victimLog } = addEnergy(
    services.energy,
    log,
    ctx.victimSeat,
    -stolen,
    "StealEnergy:Victim",
  );
  const { newState: afterThief, log: thiefLog } = addEnergy(
    afterVictim,
    victimLog,
    ctx.sourceSeat,
    stolen,
    "StealEnergy:Thief",
  );

  return {
    result: EffectResult.Applied,
    log: thiefLog,
    services: { ...services, energy: afterThief },
  };
}

function resolveAuraApply(
  e: CardEffect,
  ctx: Readonly<EffectContext>,
  services: PipelineServices,
  log: ActionLog,
): ResolverResult {
  // R5 #15 5th relock: NO GLOBAL AURA. Cards with target === "Global" are
  // remapped to the source seat — they apply ONLY to the drawer/owner.
  // Cards whose printed text reads "all players" should be authored as 3
  // individual seat-targeted effects; never fall back to a global bucket.
  const ownerSeat = ctx.sourceSeat;
  if (ownerSeat < 0) {
    console.error(`[Resolver:AuraApply] Invalid ownerSeat ${ownerSeat} for target ${e.target}`);
    const nextLog = log.append({
      kind: "resolver.failed",
      data: { kind: "AuraApply", reason: "invalid-owner-seat" } as JsonValue,
    });
    return { result: EffectResult.Failed, log: nextLog, services };
  }

  const instId = ctx.cardInstanceId;
  if (instId <= 0) {
    console.error(
      "[AuraApply] EffectContext.cardInstanceId not set (0). AuraApply must be driven by an authored card instance.",
    );
    const nextLog = log.append({
      kind: "resolver.failed",
      data: { kind: "AuraApply", reason: "cardInstanceId-not-set" } as JsonValue,
    });
    return { result: EffectResult.Failed, log: nextLog, services };
  }

  // P3 fix: use stable CardAssetId from CIS, not cardTags[0].
  let srcCard = "?";
  if (instId > 0) {
    const getResult = tryGet(services.cis, instId);
    if (getResult.ok) {
      srcCard = getResult.inst.cardAssetId;
    }
  }
  if (srcCard === "?") {
    // Fallback when CIS unavailable (legacy test path).
    srcCard = ctx.cardTags.length > 0 ? (ctx.cardTags[0] ?? "?") : "?";
  }

  // Parse categoryFilter[0] as FacilityCategory first, then FacilityType.
  // WEB.4.1: mirrors resolveBuildCostMod cascade (Unity parity: Enum.TryParse —
  // case-insensitive on both). When the raw string parses as FacilityCategory →
  // set catFilter; when it only parses as FacilityType → set ftFilter (stored
  // on AuraInstance.facilityFilter so consumers can honor the narrower match).
  // Unknown strings → log Skipped (fail-closed) and register an unfiltered aura
  // is NOT the right default here — log a warning and proceed with null filter
  // so existing unfiltered aura cards are not broken.
  let catFilter: FacilityCategory | null = null;
  let ftFilter: FacilityType | null = null;
  const catFilters = e.categoryFilter;
  if (catFilters !== undefined && catFilters.length > 0) {
    const raw = catFilters[0];
    if (raw !== undefined) {
      const parsedCat = tryParseEnumIgnoreCase(raw, FACILITY_CATEGORY_VALUES);
      if (parsedCat !== null) {
        catFilter = parsedCat as FacilityCategory;
      } else {
        const parsedFt = tryParseEnumIgnoreCase(raw, FACILITY_TYPE_VALUES);
        if (parsedFt !== null) {
          ftFilter = parsedFt as FacilityType;
        } else {
          console.warn(
            `[Resolver:AuraApply] categoryFilter '${raw}' did not parse as FacilityCategory or FacilityType — aura will match all facilities.`,
          );
        }
      }
    }
  }

  const aura: AuraInstance = {
    instanceId: instId,
    ownerSeat,
    sourceCardName: srcCard,
    effect: e,
    scope: e.auraScope ?? "BuildCost",
    isPassive: e.durationMode === "Permanent" || e.durationMode === "UntilUsed",
    categoryFilter: catFilter,
    facilityFilter: ftFilter,
  };

  const { newState: auraAfter, log: auraLog } = registerAura(services.aura, log, aura);
  return {
    result: EffectResult.Applied,
    log: auraLog,
    services: { ...services, aura: auraAfter },
  };
}

function resolveBuildCostMod(
  e: CardEffect,
  ctx: Readonly<EffectContext>,
  services: PipelineServices,
  log: ActionLog,
): ResolverResult {
  const seat = resolveSeat(e.target, ctx);
  if (seat < 0) {
    console.error(`[Resolver:BuildCostMod] No valid seat for target=${e.target}`);
    const nextLog = log.append({
      kind: "resolver.failed",
      data: { kind: "BuildCostMod", reason: "no-valid-seat", target: e.target } as JsonValue,
    });
    return { result: EffectResult.Failed, log: nextLog, services };
  }

  // Parse categoryFilter[0] as FacilityCategory first, then FacilityType.
  // Unity parity: Enum.TryParse(..., true, ...) — case-insensitive on both.
  let catFilter: FacilityCategory | null = null;
  let ftFilter: FacilityType | null = null;
  const catFilters = e.categoryFilter;
  if (catFilters !== undefined && catFilters.length > 0) {
    const raw = catFilters[0];
    if (raw !== undefined) {
      const parsedCat = tryParseEnumIgnoreCase(raw, FACILITY_CATEGORY_VALUES);
      if (parsedCat !== null) {
        catFilter = parsedCat as FacilityCategory;
      } else {
        const parsedFt = tryParseEnumIgnoreCase(raw, FACILITY_TYPE_VALUES);
        if (parsedFt !== null) {
          ftFilter = parsedFt as FacilityType;
        } else {
          console.warn(
            `[Resolver:BuildCostMod] categoryFilter '${raw}' did not parse as FacilityCategory or FacilityType — pending entry will match all builds.`,
          );
        }
      }
    }
  }

  const srcCard = ctx.cardTags.length > 0 ? (ctx.cardTags[0] ?? "?") : "?";
  const pe: PendingEffect = {
    ownerSeat: seat,
    kind: e.pendingKind ?? "NextBuildFree",
    valueInt: e.valueInt ?? 0,
    sourceCardName: srcCard,
    instanceId: ctx.cardInstanceId,
    categoryFilter: catFilter,
    facilityFilter: ftFilter,
  };

  const { newState: peqAfter, log: peqLog } = enqueue(services.peq, log, pe);
  return {
    result: EffectResult.Applied,
    log: peqLog,
    services: { ...services, peq: peqAfter },
  };
}

// ---------------------------------------------------------------------------
// Cluster F resolvers (WEB.3.A): Decommission, Destroy, StealFacility
// ---------------------------------------------------------------------------

// Decommission — drawer voluntarily tears down their own facility.
// Locked rule (Unity-Final-Snapshot §2.3 / card-rules-locked memo):
//   refund = Math.ceil(buildCostPaid * 0.5)  (50%, ceiling math).
// Caller sets ctx.targetTileIndices[0] = tile to decommission.
// Caller validation: if tile owner ≠ sourceSeat → Skipped + log reason.
function resolveDecommission(
  e: CardEffect,
  ctx: Readonly<EffectContext>,
  services: PipelineServices,
  log: ActionLog,
): ResolverResult {
  // ---------------------------------------------------------------------------
  // WEB.3.D.2 — affectsAll=true path: multi-tile Decommission.
  // Mirrors RngBranch multi-tile guard (codex WEB.3.D.1 R1 MEDIUM pattern):
  // affectsAll=true REQUIRES target=Global to prevent accidental sweep-all
  // with a non-global target (e.g. target=Self + affectsAll=true would silently
  // decom all matching facilities belonging to drawer — card-data error).
  // ---------------------------------------------------------------------------
  if (e.affectsAll === true) {
    if (e.target !== "Global") {
      const nextLog = log.append({
        kind: "resolver.skipped",
        data: {
          kind: "Decommission",
          reason: "affectsAll-requires-Global-target",
          target: e.target,
        } as JsonValue,
      });
      return { result: EffectResult.Skipped, log: nextLog, services };
    }
    return resolveDecommissionMultiTile(e, services, log);
  }

  const tileIndex = ctx.targetTileIndices[0];
  if (tileIndex === undefined) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: { kind: "Decommission", reason: "no-tile-specified" } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  const facility = getByTile(services.facilityOwnership, tileIndex);
  if (facility === null) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: { kind: "Decommission", reason: "tile-empty", tileIndex } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Decommission target switch (codex WEB.3.C R1 HIGH fix — mirror Destroy R3):
  //   • Self/Drawer       → ownerSeat must equal sourceSeat (drawer's own tile)
  //   • OpponentChosen / OpponentRandom / NearestPlayer
  //                        → ownerSeat must equal victimSeat (Climate_Protest path)
  //   • Global            → any owner (forced-decom global crisis cards)
  //   • default / unknown → Skipped (fail-closed, no fall-through)
  // Refund target = ownerSeat (the player whose facility is being decommissioned)
  // — NOT necessarily the drawer. Climate_Protest forces an opponent to decom AND
  // the opponent receives the 50% refund per locked-rule "refund to owner".
  let requiredOwner: number | null;
  switch (e.target) {
    case "Self":
    case "Drawer":
      requiredOwner = ctx.sourceSeat;
      break;
    case "OpponentChosen":
    case "OpponentRandom":
    case "NearestPlayer":
      if (ctx.victimSeat < 0) {
        const nextLog = log.append({
          kind: "resolver.skipped",
          data: {
            kind: "Decommission",
            reason: "victim-seat-not-set",
            target: e.target,
          } as JsonValue,
        });
        return { result: EffectResult.Skipped, log: nextLog, services };
      }
      requiredOwner = ctx.victimSeat;
      break;
    case "Global":
      requiredOwner = null; // any owner permitted
      break;
    default: {
      const nextLog = log.append({
        kind: "resolver.skipped",
        data: {
          kind: "Decommission",
          reason: "unsupported-target",
          target: e.target,
        } as JsonValue,
      });
      return { result: EffectResult.Skipped, log: nextLog, services };
    }
  }

  if (requiredOwner !== null && facility.ownerSeat !== requiredOwner) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "Decommission",
        reason: "wrong-owner",
        tileIndex,
        ownerSeat: facility.ownerSeat,
        requiredOwner,
        target: e.target,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Authored categoryFilter must match facility (Climate Protest = Fossil-only,
  // etc.). Codex WEB.3.A R1 HIGH fix.
  const catCheckD = checkCategoryFilter(e, facility.facilityType);
  if (!catCheckD.allowed) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "Decommission",
        reason: catCheckD.reason,
        tileIndex,
        facilityType: facility.facilityType,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Remove facility from ownership.
  let removeResult: ReturnType<typeof removeFacility>;
  try {
    removeResult = removeFacility(services.facilityOwnership, log, tileIndex);
  } catch (err) {
    if (err instanceof FacilityOwnershipError) {
      const nextLog = log.append({
        kind: "resolver.failed",
        data: { kind: "Decommission", reason: err.code, tileIndex } as JsonValue,
      });
      return { result: EffectResult.Failed, log: nextLog, services };
    }
    throw err;
  }

  // Refund = ceil(buildCostPaid * 0.5). Locked rules:
  //   • "Decommission: 50% build-cost refund to owner"
  //     (memory project_gdg_card_rules_locked.md:15)
  //   • "ModifierMath: ceil round" — global rounding policy
  //     (memory project_gdg_card_rules_locked.md:38)
  // Combination = Math.ceil(buildCostPaid * 0.5).
  // Refund recipient = facility.ownerSeat (NOT sourceSeat). Codex WEB.3.C R1 fix:
  // forced-decom cards (Climate_Protest with target=OpponentChosen) refund the
  // opponent owner, not the drawer.
  const refund = Math.ceil(facility.buildCostPaid * 0.5);
  const refundSeat = facility.ownerSeat;
  const srcLabel = `Decommission:Tile${tileIndex}`;
  const { newState: tokenAfter, log: tokenLog } = addTokens(
    services.token,
    removeResult.log,
    refundSeat,
    refund,
    srcLabel,
  );

  const nextLog = tokenLog.append({
    kind: "resolver.applied",
    data: {
      kind: "Decommission",
      sourceSeat: ctx.sourceSeat,
      ownerSeat: refundSeat,
      tileIndex,
      facilityType: facility.facilityType,
      buildCostPaid: facility.buildCostPaid,
      refund,
      target: e.target,
    } as JsonValue,
  });

  return {
    result: EffectResult.Applied,
    log: nextLog,
    services: {
      ...services,
      facilityOwnership: removeResult.newState,
      token: tokenAfter,
    },
  };
}

// Destroy — forced tear-down of another player's facility (or drawer's own via card text).
// No refund. Caller sets ctx.victimSeat (owner) + ctx.targetTileIndices[0] (tile).
// Validate: tile owner == victimSeat (else Skipped + log reason).
function resolveDestroy(
  e: CardEffect,
  ctx: Readonly<EffectContext>,
  services: PipelineServices,
  log: ActionLog,
): ResolverResult {
  const tileIndex = ctx.targetTileIndices[0];
  if (tileIndex === undefined) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: { kind: "Destroy", reason: "no-tile-specified" } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  const facility = getByTile(services.facilityOwnership, tileIndex);
  if (facility === null) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: { kind: "Destroy", reason: "tile-empty", tileIndex } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Destroy targeting per Unity locked rules:
  //   • target=Self / Drawer       → drawer (sourceSeat) is the victim, ownerSeat must match
  //   • target=OpponentChosen / OpponentRandom / NearestPlayer
  //                                  → ctx.victimSeat must match ownerSeat
  //   • target=Global              → any owner (used by global crisis cards)
  //   • all other / unknown        → Skipped (no fall-through "any owner" escape)
  // Codex WEB.3.A R1 HIGH (round 1+2): allowlist targets explicitly. R1 fixed Self,
  // R2 caught NearestPlayer (Tsunami) inheriting Global semantics by accident.
  let requiredOwner: number | null = null;
  switch (e.target) {
    case "Self":
    case "Drawer":
      requiredOwner = ctx.sourceSeat;
      break;
    case "OpponentChosen":
    case "OpponentRandom":
    case "NearestPlayer":
      if (ctx.victimSeat < 0) {
        const nextLog = log.append({
          kind: "resolver.skipped",
          data: {
            kind: "Destroy",
            reason: "victim-seat-not-set",
            target: e.target,
          } as JsonValue,
        });
        return { result: EffectResult.Skipped, log: nextLog, services };
      }
      requiredOwner = ctx.victimSeat;
      break;
    case "Global":
      requiredOwner = null;
      break;
    default: {
      const nextLog = log.append({
        kind: "resolver.skipped",
        data: {
          kind: "Destroy",
          reason: "unsupported-target",
          target: e.target,
        } as JsonValue,
      });
      return { result: EffectResult.Skipped, log: nextLog, services };
    }
  }

  if (requiredOwner !== null && facility.ownerSeat !== requiredOwner) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "Destroy",
        reason: "wrong-owner",
        tileIndex,
        ownerSeat: facility.ownerSeat,
        requiredOwner,
        target: e.target,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Authored categoryFilter must match facility (Coal Mine Collapse = Coal-only).
  // Codex WEB.3.A R1 HIGH fix.
  const catCheckDestroy = checkCategoryFilter(e, facility.facilityType);
  if (!catCheckDestroy.allowed) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "Destroy",
        reason: catCheckDestroy.reason,
        tileIndex,
        facilityType: facility.facilityType,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Remove facility — no refund.
  let removeResult: ReturnType<typeof removeFacility>;
  try {
    removeResult = removeFacility(services.facilityOwnership, log, tileIndex);
  } catch (err) {
    if (err instanceof FacilityOwnershipError) {
      const nextLog = log.append({
        kind: "resolver.failed",
        data: { kind: "Destroy", reason: err.code, tileIndex } as JsonValue,
      });
      return { result: EffectResult.Failed, log: nextLog, services };
    }
    throw err;
  }

  const nextLog = removeResult.log.append({
    kind: "resolver.applied",
    data: {
      kind: "Destroy",
      sourceSeat: ctx.sourceSeat,
      victimSeat: facility.ownerSeat,
      tileIndex,
      facilityType: facility.facilityType,
    } as JsonValue,
  });

  return {
    result: EffectResult.Applied,
    log: nextLog,
    services: { ...services, facilityOwnership: removeResult.newState },
  };
}

// StealFacility — transfer facility ownership from victimSeat to sourceSeat.
// Caller sets ctx.victimSeat + ctx.targetTileIndices[0].
// Validate: tile owner == victimSeat (else Skipped + log reason).
function resolveStealFacility(
  e: CardEffect,
  ctx: Readonly<EffectContext>,
  services: PipelineServices,
  log: ActionLog,
): ResolverResult {
  const tileIndex = ctx.targetTileIndices[0];
  if (tileIndex === undefined) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: { kind: "StealFacility", reason: "no-tile-specified" } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  if (ctx.victimSeat < 0) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: { kind: "StealFacility", reason: "no-victim-seat" } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  const facility = getByTile(services.facilityOwnership, tileIndex);
  if (facility === null) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: { kind: "StealFacility", reason: "tile-empty", tileIndex } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  if (facility.ownerSeat !== ctx.victimSeat) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "StealFacility",
        reason: "not-victim-owner",
        tileIndex,
        ownerSeat: facility.ownerSeat,
        victimSeat: ctx.victimSeat,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Authored categoryFilter must match facility. Codex WEB.3.A R1 HIGH fix.
  const catCheckSteal = checkCategoryFilter(e, facility.facilityType);
  if (!catCheckSteal.allowed) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "StealFacility",
        reason: catCheckSteal.reason,
        tileIndex,
        facilityType: facility.facilityType,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Transfer ownership to sourceSeat.
  let transferResult: ReturnType<typeof transferFacility>;
  try {
    transferResult = transferFacility(services.facilityOwnership, log, tileIndex, ctx.sourceSeat);
  } catch (err) {
    if (err instanceof FacilityOwnershipError) {
      const nextLog = log.append({
        kind: "resolver.failed",
        data: { kind: "StealFacility", reason: err.code, tileIndex } as JsonValue,
      });
      return { result: EffectResult.Failed, log: nextLog, services };
    }
    throw err;
  }

  const nextLog = transferResult.log.append({
    kind: "resolver.applied",
    data: {
      kind: "StealFacility",
      sourceSeat: ctx.sourceSeat,
      victimSeat: ctx.victimSeat,
      tileIndex,
      facilityType: facility.facilityType,
    } as JsonValue,
  });

  return {
    result: EffectResult.Applied,
    log: nextLog,
    services: { ...services, facilityOwnership: transferResult.newState },
  };
}

// ---------------------------------------------------------------------------
// resolveDecommissionMultiTile — internal helper for affectsAll=true path.
//
// WEB.3.D.2 — Labor_Union_Strikes: "DECOMMISSION ALL COAL MINES".
// Mirrors resolveRngBranchMultiTile pattern from WEB.3.D.1.
//
// Scans ALL facilities matching categoryFilter (sorted by tileIndex ascending
// for deterministic order). Decommissions each and refunds the owner.
// Aggregate result:
//   Applied  — if ≥1 matching facility decommissioned.
//   Skipped  — if 0 matching facilities found.
//
// affectsAll=true REQUIRES target=Global (same guard as RngBranch multi-tile,
// codex WEB.3.D.1 R1 MEDIUM pattern). Entry-point guard in resolveDecommission.
// ---------------------------------------------------------------------------

function resolveDecommissionMultiTile(
  e: CardEffect,
  services: PipelineServices,
  log: ActionLog,
): ResolverResult {
  const filters = e.categoryFilter;
  const matchingFacilities = services.facilityOwnership.facilities
    .filter((f) => {
      if (filters === undefined || filters.length === 0) return true;
      const facilityCategory = getCategory(f.facilityType);
      return filters.some((raw) => {
        if (raw === undefined || raw.length === 0) return false;
        const parsedCat = tryParseEnumIgnoreCase(raw, FACILITY_CATEGORY_VALUES);
        if (parsedCat !== null) return facilityCategory === parsedCat;
        const parsedFt = tryParseEnumIgnoreCase(raw, FACILITY_TYPE_VALUES);
        if (parsedFt !== null) return f.facilityType === parsedFt;
        return false;
      });
    })
    .slice()
    .sort((a, b) => a.tileIndex - b.tileIndex);

  if (matchingFacilities.length === 0) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "Decommission",
        reason: "affectsAll-no-matching-facilities",
        categoryFilter: filters ?? null,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  const startLog = log.append({
    kind: "multi-tile.start",
    data: {
      kind: "Decommission",
      affectsAll: true,
      tileCount: matchingFacilities.length,
      tileIndices: matchingFacilities.map((f) => f.tileIndex),
    } as JsonValue,
  });

  let currentServices = services;
  let currentLog = startLog;
  let decommCount = 0;
  let immunityInterceptCount = 0;

  for (const facility of matchingFacilities) {
    const tileIndex = facility.tileIndex;
    // Re-check tile still populated (previous iteration may have removed it if same tile — impossible but defensive)
    const tileOwner = getByTile(currentServices.facilityOwnership, tileIndex);
    if (tileOwner === null) continue;

    // Codex WEB.4.B R1 HIGH fix: per-tile immunity check before mutation.
    // Mirrors resolveRngBranchMultiTile per-tile pattern.
    const consumeResult = consumeIfMatches(
      currentServices.immunity,
      currentLog,
      facility.ownerSeat,
      "Decommission",
      tileIndex,
    );
    if (consumeResult.intercepted) {
      currentServices = { ...currentServices, immunity: consumeResult.newState };
      currentLog = consumeResult.log;
      immunityInterceptCount++;
      continue; // skip the mutation; this tile is shielded
    }

    let removeResult: ReturnType<typeof removeFacility>;
    try {
      removeResult = removeFacility(currentServices.facilityOwnership, currentLog, tileIndex);
    } catch (err) {
      if (err instanceof FacilityOwnershipError) {
        currentLog = currentLog.append({
          kind: "resolver.failed",
          data: { kind: "Decommission", reason: err.code, tileIndex } as JsonValue,
        });
        continue;
      }
      throw err;
    }

    const refund = Math.ceil(facility.buildCostPaid * 0.5);
    const refundSeat = facility.ownerSeat;
    const srcLabel = `Decommission:Tile${tileIndex}`;
    const { newState: tokenAfter, log: tokenLog } = addTokens(
      currentServices.token,
      removeResult.log,
      refundSeat,
      refund,
      srcLabel,
    );

    const tileLog = tokenLog.append({
      kind: "resolver.applied",
      data: {
        kind: "Decommission",
        ownerSeat: refundSeat,
        tileIndex,
        facilityType: facility.facilityType,
        buildCostPaid: facility.buildCostPaid,
        refund,
        affectsAll: true,
      } as JsonValue,
    });

    currentServices = {
      ...currentServices,
      facilityOwnership: removeResult.newState,
      token: tokenAfter,
    };
    currentLog = tileLog;
    decommCount++;
  }

  const completeLog = currentLog.append({
    kind: "multi-tile.complete",
    data: {
      kind: "Decommission",
      affectsAll: true,
      tileCount: matchingFacilities.length,
      decommCount,
      immunityInterceptCount,
      result: decommCount > 0 ? "Applied" : "Skipped",
    } as JsonValue,
  });

  return {
    result: decommCount > 0 ? EffectResult.Applied : EffectResult.Skipped,
    log: completeLog,
    services: currentServices,
  };
}

// ---------------------------------------------------------------------------
// Cluster G resolver (WEB.3.B): DiceOutcome — per-category d6 success table
// ---------------------------------------------------------------------------

// DiceOutcome — dice-driven action wrapper. Used by per-category-odds policy cards:
//   Mercenaries  (STEAL ANY FACILITY): Fossil 4/6, Renewable 3/6, Nuclear/Dams 2/6
//   Spy Sabotage II (DESTROY 1 FACILITY): same odds table
//
// Resolver behavior:
//   1. Check target tile supplied (targetTileIndices[0]) — else Skipped.
//   2. Look up facility at that tile.
//   3. Look up facility's FacilityCategory → find threshold in effect.diceTable.
//      If no diceTable entry for category → use chancePct/100 * 6 fallback → else Skipped.
//   4. Roll d6 via services.rng.nextInt(1, 6).
//   5. If roll ≤ threshold → success:
//        • "Destroy" on-hit: call resolveDestroy semantics (remove facility, no refund).
//        • "StealFacility" on-hit: call resolveStealFacility semantics (transfer to sourceSeat).
//        • On-hit action determined by effect.target context + card-level tags (see below).
//   6. Append ActionLog entry with roll, threshold, category, applied.
//
// On-hit action dispatch: DiceOutcome is a wrapper — the effect's "on-hit" action is
// encoded in a sub-effect via the `pendingKind` / `categoryFilter` / `diceOnHit` approach.
// For simplicity (WEB.3.B pure-Cluster G scope), the on-hit behavior is determined by
// the DiceOutcome effect's own `valueInt` coded as a discriminant:
//   valueInt = 1 → StealFacility on success
//   valueInt = 2 → Destroy on success
// (Cards-level authoring convention; enforced in cards.json.)
//
// Caller threading: rng MUST be the authoritative seeded Rng from PipelineServices.rng.
// Same seed → same roll → deterministic replay (WEB.6 requirement).
//
// Locked rules for on-hit Destroy:
//   • Allowlist targets same as resolveDestroy (OpponentChosen/OpponentRandom/NearestPlayer/Global)
//   • categoryFilter on the DiceOutcome effect is the eligible-category list (same as diceTable keys)
//   • No refund (Destroy semantics)
//
// Locked rules for on-hit StealFacility:
//   • victimSeat must be set by caller (not -1); owner must equal victimSeat
//   • categoryFilter on the DiceOutcome effect is the eligible-category list
//   • Ownership transferred to sourceSeat

// DiceOutcome on-hit discriminant (valueInt field)
const DICE_ONHIT_STEAL_FACILITY = 1;
const DICE_ONHIT_DESTROY = 2;

function resolveDiceOutcome(
  e: CardEffect,
  ctx: Readonly<EffectContext>,
  services: PipelineServices,
  log: ActionLog,
): ResolverResult {
  // Guard: tile must be specified
  const tileIndex = ctx.targetTileIndices[0];
  if (tileIndex === undefined) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: { kind: "DiceOutcome", reason: "no-tile-specified" } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Guard: facility must exist at tile
  const facility = getByTile(services.facilityOwnership, tileIndex);
  if (facility === null) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: { kind: "DiceOutcome", reason: "tile-empty", tileIndex } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Guard: categoryFilter is an ALLOWLIST for DiceOutcome (OR semantics — any entry matches = eligible).
  // Differs from Cluster F's single-entry restriction (checkCategoryFilter uses filters[0] only).
  // DiceOutcome on Mercenaries/SpySabotageII lists ALL targetable categories explicitly.
  // If categoryFilter is absent or empty, any category is eligible.
  // Each token in categoryFilter may be a FacilityCategory OR FacilityType (case-insensitive).
  const facilityCategory = getCategory(facility.facilityType);
  const filters = e.categoryFilter;
  if (filters !== undefined && filters.length > 0) {
    const eligible = filters.some((raw) => {
      if (raw === undefined || raw.length === 0) return false;
      // Try FacilityCategory first
      const parsedCat = tryParseEnumIgnoreCase(raw, FACILITY_CATEGORY_VALUES);
      if (parsedCat !== null) return facilityCategory === parsedCat;
      // Try FacilityType
      const parsedFt = tryParseEnumIgnoreCase(raw, FACILITY_TYPE_VALUES);
      if (parsedFt !== null) return facility.facilityType === parsedFt;
      return false;
    });
    if (!eligible) {
      const nextLog = log.append({
        kind: "resolver.skipped",
        data: {
          kind: "DiceOutcome",
          reason: "category-not-in-allowlist",
          tileIndex,
          facilityType: facility.facilityType,
          category: facilityCategory,
        } as JsonValue,
      });
      return { result: EffectResult.Skipped, log: nextLog, services };
    }
  }

  // Determine per-FacilityType-or-Category threshold from diceTable.
  // Lookup priority (codex WEB.3.B R1 HIGH fix):
  //   1. FacilityType key (e.g. "Hydro": 2) — most specific. Source card text
  //      "DAMS NUCLEAR 2/6" splits Hydro out from generic "RENEWABLES 3/6".
  //   2. FacilityCategory key (e.g. "Renewable": 3) — fallback when no
  //      type-specific override.
  // Fallback: if diceTable absent but chancePct set, compute threshold = Math.ceil(chancePct/100*6).
  let threshold: number | null = null;

  if (e.diceTable !== undefined) {
    // FacilityType override (most specific) wins over category default.
    const typeRaw = e.diceTable[facility.facilityType];
    if (typeRaw !== undefined && Number.isInteger(typeRaw) && typeRaw >= 1 && typeRaw <= 6) {
      threshold = typeRaw;
    } else {
      const catRaw = e.diceTable[facilityCategory];
      if (catRaw !== undefined && Number.isInteger(catRaw) && catRaw >= 1 && catRaw <= 6) {
        threshold = catRaw;
      }
    }
  }

  if (threshold === null && e.chancePct !== undefined && e.chancePct > 0) {
    // Flat percent → nearest d6 threshold (ceil so 50% = 3/6 not 2/6).
    threshold = Math.ceil((e.chancePct / 100) * 6);
    if (threshold < 1) threshold = 1;
    if (threshold > 6) threshold = 6;
  }

  if (threshold === null) {
    // No dice data for this category — treat as ineligible
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "DiceOutcome",
        reason: "no-threshold-for-category",
        category: facilityCategory,
        tileIndex,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Roll d6 using the seeded Rng from PipelineServices (deterministic replay guarantee).
  const roll = services.rng.nextInt(1, 6);
  const hit = roll <= threshold;

  if (!hit) {
    // Dice miss — Skipped with structured log
    const nextLog = log.append({
      kind: "dice.miss",
      data: {
        kind: "DiceOutcome",
        tileIndex,
        facilityType: facility.facilityType,
        category: facilityCategory,
        roll,
        threshold,
        applied: false,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Dice hit — dispatch to on-hit action (StealFacility / Destroy).
  // Codex WEB.3.B R1 MEDIUM fix: fail closed on missing/unknown valueInt
  // instead of silently defaulting to Destroy.
  if (e.valueInt === undefined) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: { kind: "DiceOutcome", reason: "no-onhit-action", tileIndex } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }
  if (e.valueInt !== DICE_ONHIT_STEAL_FACILITY && e.valueInt !== DICE_ONHIT_DESTROY) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "DiceOutcome",
        reason: "unsupported-onhit-action",
        valueInt: e.valueInt,
        tileIndex,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }
  const onHit = e.valueInt;

  if (onHit === DICE_ONHIT_STEAL_FACILITY) {
    // Target allowlist for StealFacility on-hit (codex WEB.3.B R1 MEDIUM fix —
    // mirror parity with Destroy branch). Steal MUST come from a victim seat.
    switch (e.target) {
      case "OpponentChosen":
      case "OpponentRandom":
      case "NearestPlayer":
        break; // allowed
      default: {
        const nextLog = log.append({
          kind: "resolver.skipped",
          data: {
            kind: "DiceOutcome",
            reason: "steal-unsupported-target",
            target: e.target,
          } as JsonValue,
        });
        return { result: EffectResult.Skipped, log: nextLog, services };
      }
    }
    // StealFacility on-hit: caller must supply victimSeat = facility.ownerSeat
    if (ctx.victimSeat < 0) {
      const nextLog = log.append({
        kind: "resolver.skipped",
        data: { kind: "DiceOutcome", reason: "steal-no-victim-seat", tileIndex } as JsonValue,
      });
      return { result: EffectResult.Skipped, log: nextLog, services };
    }
    if (facility.ownerSeat !== ctx.victimSeat) {
      const nextLog = log.append({
        kind: "resolver.skipped",
        data: {
          kind: "DiceOutcome",
          reason: "steal-wrong-owner",
          tileIndex,
          ownerSeat: facility.ownerSeat,
          victimSeat: ctx.victimSeat,
        } as JsonValue,
      });
      return { result: EffectResult.Skipped, log: nextLog, services };
    }

    let transferResult: ReturnType<typeof transferFacility>;
    try {
      transferResult = transferFacility(services.facilityOwnership, log, tileIndex, ctx.sourceSeat);
    } catch (err) {
      if (err instanceof FacilityOwnershipError) {
        const nextLog = log.append({
          kind: "resolver.failed",
          data: { kind: "DiceOutcome", reason: err.code, tileIndex } as JsonValue,
        });
        return { result: EffectResult.Failed, log: nextLog, services };
      }
      throw err;
    }

    const nextLog = transferResult.log.append({
      kind: "dice.hit",
      data: {
        kind: "DiceOutcome",
        onHit: "StealFacility",
        tileIndex,
        facilityType: facility.facilityType,
        category: facilityCategory,
        roll,
        threshold,
        applied: true,
        sourceSeat: ctx.sourceSeat,
        victimSeat: ctx.victimSeat,
      } as JsonValue,
    });
    return {
      result: EffectResult.Applied,
      log: nextLog,
      services: { ...services, facilityOwnership: transferResult.newState },
    };
  }

  // Default on-hit: Destroy (no refund)
  // Target ownership check: same allowlist as resolveDestroy.
  let requiredOwner: number | null = null;
  switch (e.target) {
    case "Self":
    case "Drawer":
      requiredOwner = ctx.sourceSeat;
      break;
    case "OpponentChosen":
    case "OpponentRandom":
    case "NearestPlayer":
      if (ctx.victimSeat < 0) {
        const nextLog = log.append({
          kind: "resolver.skipped",
          data: {
            kind: "DiceOutcome",
            reason: "destroy-victim-seat-not-set",
            target: e.target,
          } as JsonValue,
        });
        return { result: EffectResult.Skipped, log: nextLog, services };
      }
      requiredOwner = ctx.victimSeat;
      break;
    case "Global":
      requiredOwner = null;
      break;
    default: {
      const nextLog = log.append({
        kind: "resolver.skipped",
        data: {
          kind: "DiceOutcome",
          reason: "destroy-unsupported-target",
          target: e.target,
        } as JsonValue,
      });
      return { result: EffectResult.Skipped, log: nextLog, services };
    }
  }

  if (requiredOwner !== null && facility.ownerSeat !== requiredOwner) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "DiceOutcome",
        reason: "destroy-wrong-owner",
        tileIndex,
        ownerSeat: facility.ownerSeat,
        requiredOwner,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  let removeResult: ReturnType<typeof removeFacility>;
  try {
    removeResult = removeFacility(services.facilityOwnership, log, tileIndex);
  } catch (err) {
    if (err instanceof FacilityOwnershipError) {
      const nextLog = log.append({
        kind: "resolver.failed",
        data: { kind: "DiceOutcome", reason: err.code, tileIndex } as JsonValue,
      });
      return { result: EffectResult.Failed, log: nextLog, services };
    }
    throw err;
  }

  const nextLog = removeResult.log.append({
    kind: "dice.hit",
    data: {
      kind: "DiceOutcome",
      onHit: "Destroy",
      tileIndex,
      facilityType: facility.facilityType,
      category: facilityCategory,
      roll,
      threshold,
      applied: true,
      sourceSeat: ctx.sourceSeat,
      victimSeat: facility.ownerSeat,
    } as JsonValue,
  });
  return {
    result: EffectResult.Applied,
    log: nextLog,
    services: { ...services, facilityOwnership: removeResult.newState },
  };
}

// ---------------------------------------------------------------------------
// Cluster L resolver (WEB.3.C): RngBranch — probability fork
//
// Canonical Cluster L cards with RngBranch:
//   Fukushima Disaster / Chernobyl Incident:
//     "ALL NUCLEAR PLANTS ENTER LOCKDOWN / 50% CHANCE OF DECOMMISSION"
//   Wildfire Near Facilities:
//     "PAY 15 TOKEN TO REBUILD / OR / RISK DESTRUCTION (50% CHANCE OF DECOMMISSION, +1 RISK METER)"
//   Nuclear Safety Regulation:
//     "PAY 15 TOKENS/PLANT UPKEEP / OR / RISK 50% CHANCE OF MELTDOWN (DECOMMISSION, +20 POLLUTION)"
//
// On-hit action discriminant encoded in effect.valueInt (fail-closed, no default):
//   1 = Decommission  (remove facility, 50% refund to owner)
//   2 = Destroy       (remove facility, no refund)
//   3 = Decommission + PollutionDelta (value = effect.add, global)
//       Used by Nuclear Safety Regulation: Decommission + +20 pollution on meltdown.
//
// effect.chancePct — probability 0–100.
// Roll = services.rng.nextInt(1, 100). Hit if roll <= chancePct.
// 50% → roll ≤ 50 → exactly half of [1..100] → canonical 50/50.
//
// effect.categoryFilter — allowlist for eligible facility types/categories
//   (OR semantics, same as DiceOutcome). If absent/empty, any facility is eligible.
//
// Target allowlist (explicit — no fall-through):
//   Self / Drawer    → drawer owns tile (ctx.sourceSeat must be facility.ownerSeat)
//   OpponentChosen / OpponentRandom / NearestPlayer / Global
//                    → victim-based or any-owner (same as Destroy target semantics)
//   All other targets → Skipped (no destructive fall-through default)
//
// Locked rules:
//   - WEB.3.A R2: explicit target allowlist + default-Skipped
//   - WEB.3.B R1: fail-closed dispatch on unknown valueInt (never ?? to destructive)
//   - rounding: Math.ceil (project_gdg_card_rules_locked.md:38)

// RngBranch on-hit discriminant (valueInt field)
const RNG_ONHIT_DECOMMISSION = 1; // 50% refund to owner
const RNG_ONHIT_DESTROY = 2; // no refund
const RNG_ONHIT_DECOMMISSION_POLLUTION = 3; // Decommission + pollution (add field = pollution amount)

// ---------------------------------------------------------------------------
// resolveRngBranchSingleTile — internal helper for the single-tile path.
// Called by resolveRngBranch when affectsAll is absent/false.
// Also called by resolveRngBranchMultiTile for each individual tile.
// Returns ResolverResult for that tile.
//
// Pre-conditions (caller must verify):
//   - chancePct is defined and > 0
//   - onHit is a known discriminant (1/2/3)
//   - facility exists at tileIndex
//   - facilityCategory passes categoryFilter (or filter is absent)
//   - owner passes requiredOwner constraint (or requiredOwner is null)
// ---------------------------------------------------------------------------

function resolveRngBranchSingleTile(
  e: CardEffect,
  tileIndex: number,
  chancePct: number,
  onHit: number,
  services: PipelineServices,
  log: ActionLog,
): ResolverResult {
  const facility = getByTile(services.facilityOwnership, tileIndex);
  if (facility === null) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: { kind: "RngBranch", reason: "tile-empty", tileIndex } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }
  const facilityCategory = getCategory(facility.facilityType);

  const roll = services.rng.nextInt(1, 100);
  const hit = roll <= chancePct;

  const rollLog = log.append({
    kind: "rng.rolled",
    data: {
      kind: "RngBranch",
      tileIndex,
      facilityType: facility.facilityType,
      category: facilityCategory,
      roll,
      chancePct,
      applied: hit,
    } as JsonValue,
  });

  if (!hit) {
    const nextLog = rollLog.append({
      kind: "rng.miss",
      data: {
        kind: "RngBranch",
        tileIndex,
        facilityType: facility.facilityType,
        roll,
        chancePct,
        applied: false,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Hit — apply on-hit action
  if (onHit === RNG_ONHIT_DECOMMISSION || onHit === RNG_ONHIT_DECOMMISSION_POLLUTION) {
    let removeResult: ReturnType<typeof removeFacility>;
    try {
      removeResult = removeFacility(services.facilityOwnership, rollLog, tileIndex);
    } catch (err) {
      if (err instanceof FacilityOwnershipError) {
        const nextLog = rollLog.append({
          kind: "resolver.failed",
          data: { kind: "RngBranch", reason: err.code, tileIndex } as JsonValue,
        });
        return { result: EffectResult.Failed, log: nextLog, services };
      }
      throw err;
    }

    const refund = Math.ceil(facility.buildCostPaid * 0.5);
    const srcLabel = `RngBranch:Tile${tileIndex}`;
    const { newState: tokenAfter, log: tokenLog } = addTokens(
      services.token,
      removeResult.log,
      facility.ownerSeat,
      refund,
      srcLabel,
    );

    let currentServices: PipelineServices = {
      ...services,
      facilityOwnership: removeResult.newState,
      token: tokenAfter,
    };
    let currentLog = tokenLog;

    if (onHit === RNG_ONHIT_DECOMMISSION_POLLUTION) {
      const pollutionAmount = e.add ?? 0;
      if (pollutionAmount !== 0) {
        const srcLabelPoll = `RngBranch:Meltdown:Tile${tileIndex}`;
        const { newState: pollAfter, log: pollLog } = addPollution(
          currentServices.pollution,
          currentLog,
          pollutionAmount,
          srcLabelPoll,
          null, // global crisis — no single seat owner
        );
        currentServices = { ...currentServices, pollution: pollAfter };
        currentLog = pollLog;
      }
    }

    const hitLog = currentLog.append({
      kind: "rng.hit",
      data: {
        kind: "RngBranch",
        onHit: onHit === RNG_ONHIT_DECOMMISSION ? "Decommission" : "Decommission+Pollution",
        tileIndex,
        facilityType: facility.facilityType,
        category: facilityCategory,
        roll,
        chancePct,
        applied: true,
        ownerSeat: facility.ownerSeat,
        refund,
      } as JsonValue,
    });
    return { result: EffectResult.Applied, log: hitLog, services: currentServices };
  }

  // onHit === RNG_ONHIT_DESTROY: remove facility, no refund
  let removeResult: ReturnType<typeof removeFacility>;
  try {
    removeResult = removeFacility(services.facilityOwnership, rollLog, tileIndex);
  } catch (err) {
    if (err instanceof FacilityOwnershipError) {
      const nextLog = rollLog.append({
        kind: "resolver.failed",
        data: { kind: "RngBranch", reason: err.code, tileIndex } as JsonValue,
      });
      return { result: EffectResult.Failed, log: nextLog, services };
    }
    throw err;
  }

  const hitLog = removeResult.log.append({
    kind: "rng.hit",
    data: {
      kind: "RngBranch",
      onHit: "Destroy",
      tileIndex,
      facilityType: facility.facilityType,
      category: facilityCategory,
      roll,
      chancePct,
      applied: true,
      ownerSeat: facility.ownerSeat,
    } as JsonValue,
  });
  return {
    result: EffectResult.Applied,
    log: hitLog,
    services: { ...services, facilityOwnership: removeResult.newState },
  };
}

// ---------------------------------------------------------------------------
// resolveRngBranchMultiTile — internal helper for affectsAll=true path.
//
// Scans ALL facilities matching categoryFilter, sorted by tileIndex ascending
// (deterministic order regardless of insertion order). Rolls RNG independently
// per tile. Aggregate result:
//   Applied  — if ≥1 tile hit (removed/refunded/polluted)
//   Skipped  — if 0 matching facilities OR all tiles missed
//
// ActionLog: one rng.rolled entry per tile (before branching), one rng.hit or
// rng.miss entry per tile. Plus multi-tile.start / multi-tile.complete bookend
// entries for replay tracing.
//
// Pollution: per-tile (each decommissioned plant adds its own +20 pollution
// wave; matches "MELTDOWN" intuition — each reactor leaks independently).
// ---------------------------------------------------------------------------

function resolveRngBranchMultiTile(
  e: CardEffect,
  chancePct: number,
  onHit: number,
  services: PipelineServices,
  log: ActionLog,
): ResolverResult {
  // Filter all facilities matching categoryFilter (OR semantics, same as single-tile)
  const filters = e.categoryFilter;
  const matchingFacilities = services.facilityOwnership.facilities
    .filter((f) => {
      if (filters === undefined || filters.length === 0) return true;
      const facilityCategory = getCategory(f.facilityType);
      return filters.some((raw) => {
        if (raw === undefined || raw.length === 0) return false;
        const parsedCat = tryParseEnumIgnoreCase(raw, FACILITY_CATEGORY_VALUES);
        if (parsedCat !== null) return facilityCategory === parsedCat;
        const parsedFt = tryParseEnumIgnoreCase(raw, FACILITY_TYPE_VALUES);
        if (parsedFt !== null) return f.facilityType === parsedFt;
        return false;
      });
    })
    // Sort by tileIndex ascending — deterministic RNG consumption order
    .slice()
    .sort((a, b) => a.tileIndex - b.tileIndex);

  if (matchingFacilities.length === 0) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "RngBranch",
        reason: "affectsAll-no-matching-facilities",
        categoryFilter: filters ?? null,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  const startLog = log.append({
    kind: "multi-tile.start",
    data: {
      kind: "RngBranch",
      affectsAll: true,
      tileCount: matchingFacilities.length,
      tileIndices: matchingFacilities.map((f) => f.tileIndex),
      chancePct,
    } as JsonValue,
  });

  let currentServices = services;
  let currentLog = startLog;
  let hitCount = 0;
  let immunityInterceptCount = 0;

  for (const facility of matchingFacilities) {
    // Codex WEB.4.B R1 HIGH fix: per-tile immunity check before mutation.
    // The pipeline-level immunity check at runSingleEffect handles single-target
    // effects but bypasses multi-tile sweeps (target=Global → no seat to check
    // OR victimSeat=-1). Each owner of a matching tile may have their own
    // Immunity entry registered. Consume per-owner-per-tile and skip mutation
    // when intercepted.
    const consumeResult = consumeIfMatches(
      currentServices.immunity,
      currentLog,
      facility.ownerSeat,
      "RngBranch",
      facility.tileIndex,
    );
    if (consumeResult.intercepted) {
      currentServices = { ...currentServices, immunity: consumeResult.newState };
      currentLog = consumeResult.log;
      immunityInterceptCount++;
      continue; // skip the mutation; this tile is shielded
    }

    const tileResult = resolveRngBranchSingleTile(
      e,
      facility.tileIndex,
      chancePct,
      onHit,
      currentServices,
      currentLog,
    );
    currentServices = tileResult.services;
    currentLog = tileResult.log;
    if (tileResult.result === EffectResult.Applied) hitCount++;
    // Failed on a tile does NOT abort the multi-tile sweep — we continue
    // so other tiles still get their roll. The failed tile is logged above.
  }

  const completeLog = currentLog.append({
    kind: "multi-tile.complete",
    data: {
      kind: "RngBranch",
      affectsAll: true,
      tileCount: matchingFacilities.length,
      hitCount,
      immunityInterceptCount,
      result: hitCount > 0 ? "Applied" : "Skipped",
    } as JsonValue,
  });

  return {
    result: hitCount > 0 ? EffectResult.Applied : EffectResult.Skipped,
    log: completeLog,
    services: currentServices,
  };
}

function resolveRngBranch(
  e: CardEffect,
  ctx: Readonly<EffectContext>,
  services: PipelineServices,
  log: ActionLog,
): ResolverResult {
  // Guard: chancePct must be set and > 0
  const chancePct = e.chancePct;
  if (chancePct === undefined || chancePct <= 0) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: { kind: "RngBranch", reason: "no-chance-pct" } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Guard: on-hit action must be set and known (fail-closed — WEB.3.B R1 pattern).
  // Validated early so both single-tile and multi-tile paths share the same guard.
  if (e.valueInt === undefined) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: { kind: "RngBranch", reason: "no-onhit-action" } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }
  if (
    e.valueInt !== RNG_ONHIT_DECOMMISSION &&
    e.valueInt !== RNG_ONHIT_DESTROY &&
    e.valueInt !== RNG_ONHIT_DECOMMISSION_POLLUTION
  ) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "RngBranch",
        reason: "unsupported-onhit-action",
        valueInt: e.valueInt,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }
  const onHit = e.valueInt;

  // ---------------------------------------------------------------------------
  // Multi-tile path (WEB.3.D.1): affectsAll=true
  // Multi-tile semantics is "Global crisis affecting ALL matching tiles
  // regardless of owner" — the ONLY valid target is Global. Any other target
  // with affectsAll=true is a card-data error (codex WEB.3.D.1 R1 MEDIUM fix).
  // Reject fail-closed before scanning state — preserves the WEB.3.A R3 +
  // WEB.3.B R1 + WEB.3.C R2 fail-closed-target pattern.
  // ---------------------------------------------------------------------------
  if (e.affectsAll === true) {
    if (e.target !== "Global") {
      const nextLog = log.append({
        kind: "resolver.skipped",
        data: {
          kind: "RngBranch",
          reason: "affectsAll-requires-Global-target",
          target: e.target,
        } as JsonValue,
      });
      return { result: EffectResult.Skipped, log: nextLog, services };
    }
    return resolveRngBranchMultiTile(e, chancePct, onHit, services, log);
  }

  // ---------------------------------------------------------------------------
  // Single-tile path (legacy — unchanged from WEB.3.C).
  // ---------------------------------------------------------------------------

  // Guard: tile must be specified (facility target)
  const tileIndex = ctx.targetTileIndices[0];
  if (tileIndex === undefined) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: { kind: "RngBranch", reason: "no-tile-specified" } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Guard: facility must exist at tile
  const facility = getByTile(services.facilityOwnership, tileIndex);
  if (facility === null) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: { kind: "RngBranch", reason: "tile-empty", tileIndex } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // categoryFilter allowlist (OR semantics, same as DiceOutcome).
  // If absent/empty, any category is eligible.
  const facilityCategory = getCategory(facility.facilityType);
  const filters = e.categoryFilter;
  if (filters !== undefined && filters.length > 0) {
    const eligible = filters.some((raw) => {
      if (raw === undefined || raw.length === 0) return false;
      const parsedCat = tryParseEnumIgnoreCase(raw, FACILITY_CATEGORY_VALUES);
      if (parsedCat !== null) return facilityCategory === parsedCat;
      const parsedFt = tryParseEnumIgnoreCase(raw, FACILITY_TYPE_VALUES);
      if (parsedFt !== null) return facility.facilityType === parsedFt;
      return false;
    });
    if (!eligible) {
      const nextLog = log.append({
        kind: "resolver.skipped",
        data: {
          kind: "RngBranch",
          reason: "category-not-in-allowlist",
          tileIndex,
          facilityType: facility.facilityType,
          category: facilityCategory,
        } as JsonValue,
      });
      return { result: EffectResult.Skipped, log: nextLog, services };
    }
  }

  // Explicit target allowlist — determine required owner (fail-closed).
  // Mirrors Destroy target semantics (WEB.3.A R2 lesson).
  let requiredOwner: number | null = null;
  switch (e.target) {
    case "Self":
    case "Drawer":
      requiredOwner = ctx.sourceSeat;
      break;
    case "OpponentChosen":
    case "OpponentRandom":
    case "NearestPlayer":
      if (ctx.victimSeat < 0) {
        const nextLog = log.append({
          kind: "resolver.skipped",
          data: {
            kind: "RngBranch",
            reason: "victim-seat-not-set",
            target: e.target,
          } as JsonValue,
        });
        return { result: EffectResult.Skipped, log: nextLog, services };
      }
      requiredOwner = ctx.victimSeat;
      break;
    case "Global":
      requiredOwner = null;
      break;
    default: {
      const nextLog = log.append({
        kind: "resolver.skipped",
        data: {
          kind: "RngBranch",
          reason: "unsupported-target",
          target: e.target,
        } as JsonValue,
      });
      return { result: EffectResult.Skipped, log: nextLog, services };
    }
  }

  if (requiredOwner !== null && facility.ownerSeat !== requiredOwner) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "RngBranch",
        reason: "wrong-owner",
        tileIndex,
        ownerSeat: facility.ownerSeat,
        requiredOwner,
        target: e.target,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Delegate to single-tile helper (roll + apply on-hit)
  return resolveRngBranchSingleTile(e, tileIndex, chancePct, onHit, services, log);
}

// ---------------------------------------------------------------------------
// WEB.4.B — Immunity resolver (promoted from deferred stub)
//
// Reads effect.immuneAgainstKinds to determine what this Immunity instance
// shields against. Registers a new ImmunityInstance into PipelineServices.immunity.
//
// Scope:
//   target=Self / target=Drawer → per-seat immunity (ownerSeat=sourceSeat, tileIndex=null)
//   No per-tile scope at registration time: Enhanced Measures and Infrastructure
//   Bailout protect "1 FACILITY" but the tile is chosen at interception time
//   (ctx.targetTileIndices[0] when the threatening effect is played), NOT at
//   registration time.  Thus registration always uses tileIndex=null and the
//   pipeline's consumeIfMatches call passes the tile being targeted.
//
//   Per card text: all 9 Immunity cards use Self or Drawer as target.
//   Other targets → Skipped (fail-closed allowlist).
//
// valueInt = chargesRemaining. Convention:
//   valueInt = 1  → single-use (Enhanced Measures, Infrastructure Bailout)
//   valueInt = 999 → permanent sentinel (UN Green Pact, Microgrids, Blockchain,
//                     Decentralized Grid, Diplomatic Immunity)
//
// durationMode drives isPassive:
//   durationMode = "Permanent" → isPassive = true
//   durationMode = "UntilUsed" → isPassive = false (consumed on first use)
// ---------------------------------------------------------------------------

import { IMMUNITY_PERMANENT_CHARGES, registerImmunity } from "../services/immunity-service.js";
import type { ImmunityInstance } from "../services/immunity-service.js";

function resolveImmunity(
  e: CardEffect,
  ctx: Readonly<EffectContext>,
  services: PipelineServices,
  log: ActionLog,
): ResolverResult {
  // Fail-closed target allowlist.
  let ownerSeat: number;
  switch (e.target) {
    case "Self":
    case "Drawer":
      ownerSeat = ctx.sourceSeat;
      break;
    default: {
      const nextLog = log.append({
        kind: "resolver.skipped",
        data: {
          kind: "Immunity",
          reason: "unsupported-target",
          target: e.target,
        } as JsonValue,
      });
      return { result: EffectResult.Skipped, log: nextLog, services };
    }
  }

  // immuneAgainstKinds must be present and non-empty.
  const againstKinds = e.immuneAgainstKinds;
  if (againstKinds === undefined || againstKinds.length === 0) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "Immunity",
        reason: "no-immuneAgainstKinds",
        target: e.target,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // chargesRemaining from valueInt. Default sentinel = permanent.
  const charges =
    e.valueInt !== undefined && e.valueInt > 0 ? e.valueInt : IMMUNITY_PERMANENT_CHARGES;

  const isPassive = e.durationMode === "Permanent";

  const instanceId = ctx.cardInstanceId > 0 ? ctx.cardInstanceId : 0;
  const sourceCardName =
    ctx.cardTags.length > 0 ? (ctx.cardTags[0] ?? "ImmunityCard") : "ImmunityCard";

  const instance: ImmunityInstance = {
    instanceId,
    ownerSeat,
    tileIndex: null, // seat-level immunity; per-tile check happens at consumeIfMatches
    againstKinds,
    sourceCardName,
    chargesRemaining: charges,
    isPassive,
  };

  let regResult: ReturnType<typeof registerImmunity>;
  try {
    regResult = registerImmunity(services.immunity, log, instance);
  } catch (err) {
    const nextLog = log.append({
      kind: "resolver.failed",
      data: {
        kind: "Immunity",
        reason: String(err instanceof Error ? err.message : err),
      } as JsonValue,
    });
    return { result: EffectResult.Failed, log: nextLog, services };
  }

  const nextLog = regResult.log.append({
    kind: "resolver.applied",
    data: {
      kind: "Immunity",
      instanceId,
      ownerSeat,
      againstKinds: [...againstKinds],
      charges,
      isPassive,
      sourceCardName,
    } as JsonValue,
  });

  return {
    result: EffectResult.Applied,
    log: nextLog,
    services: { ...services, immunity: regResult.newState },
  };
}

// ---------------------------------------------------------------------------
// WEB.4.C: StealCard, CancelDisaster, FacilityTransfer, DrawCard
// ---------------------------------------------------------------------------

// --- WEB.4.C resolver imports ---
import { MAX_HAND_SIZE, tryRegisterAuraPassive } from "../services/card-acquisition-service.js";

import { drawFromPool, loadCards } from "../services/card-deck-manager.js";
import {
  CardZone,
  createForSeat,
  getByOwner as getCISByOwner,
  moveZone,
  transferOwner,
} from "../services/card-instance-service.js";

// ---------------------------------------------------------------------------
// StealCard — WEB.4.C
//
// Randomly steals one card from victim's Hand zone and moves it to drawer's Hand.
// Target allowlist: OpponentChosen / OpponentRandom / NearestPlayer (no Self-steal).
// Optional cardPoolFilter (categoryFilter repurposed as card-type filter):
//   categoryFilter: ["Policy"] → only steal Policy-type cards
//   categoryFilter: []         → steal any card type
// Edge: empty victim hand (filtered or total) → Skipped + structured log reason.
// Transfer: update CIS ownerSeat + zone remains Hand (card is now in drawer's hand).
// seeded RNG for deterministic pick: rng.nextInt(0, eligibleCount-1).
// ---------------------------------------------------------------------------
function resolveStealCard(
  e: CardEffect,
  ctx: Readonly<EffectContext>,
  services: PipelineServices,
  log: ActionLog,
): ResolverResult {
  // Fail-closed target allowlist (Self-steal disallowed).
  let victimSeat: number;
  switch (e.target) {
    case "OpponentChosen":
    case "OpponentRandom":
    case "NearestPlayer":
      if (ctx.victimSeat < 0) {
        const nextLog = log.append({
          kind: "resolver.skipped",
          data: {
            kind: "StealCard",
            reason: "victim-seat-not-set",
            target: e.target,
          } as JsonValue,
        });
        return { result: EffectResult.Skipped, log: nextLog, services };
      }
      victimSeat = ctx.victimSeat;
      break;
    default: {
      const nextLog = log.append({
        kind: "resolver.skipped",
        data: {
          kind: "StealCard",
          reason: "unsupported-target",
          target: e.target,
        } as JsonValue,
      });
      return { result: EffectResult.Skipped, log: nextLog, services };
    }
  }

  // Prevent stealing from oneself (target mismatch guard).
  if (victimSeat === ctx.sourceSeat) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "StealCard",
        reason: "self-steal-blocked",
        victimSeat,
        sourceSeat: ctx.sourceSeat,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Codex WEB.4.C R1 LOW: drawer hand-full check moved BEFORE RNG consumption
  // so a Skipped path doesn't perturb the seeded sequence.
  const drawerHandCount = getCISByOwner(services.cis, ctx.sourceSeat).filter(
    (inst) => inst.zone === CardZone.Hand,
  ).length;
  if (drawerHandCount >= MAX_HAND_SIZE) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "StealCard",
        reason: "drawer-hand-full",
        drawerSeat: ctx.sourceSeat,
        drawerHandCount,
        MAX_HAND_SIZE,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Gather victim's Hand-zone instances.
  const allVictimInstances = getCISByOwner(services.cis, victimSeat).filter(
    (inst) => inst.zone === CardZone.Hand,
  );

  // Codex WEB.4.C R1 MEDIUM: use cardPoolFilter (new schema field) not categoryFilter.
  // categoryFilter is for FacilityCategory/Type; cardPoolFilter is for CardType.
  const typeFilter =
    e.cardPoolFilter !== undefined && e.cardPoolFilter.length > 0
      ? (e.cardPoolFilter[0] ?? null)
      : null;

  // Load all card defs for type-filter lookup (cached — module-level frozen constant).
  const allCards = loadCards().cards;

  let eligibleInstances = allVictimInstances;
  if (typeFilter !== null) {
    eligibleInstances = allVictimInstances.filter((inst) => {
      const def = allCards.find((c) => c.name === inst.cardAssetId);
      return def !== undefined && def.cardType === typeFilter;
    });
  }

  if (eligibleInstances.length === 0) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "StealCard",
        reason: allVictimInstances.length === 0 ? "victim-hand-empty" : "no-eligible-cards",
        victimSeat,
        typeFilter,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Seeded RNG pick: deterministic random index. (Hand-full guard above means
  // we only consume RNG when an Applied path is reachable.)
  const pickIdx = services.rng.nextInt(0, eligibleInstances.length - 1);
  const chosenInst = eligibleInstances[pickIdx];
  if (chosenInst === undefined) {
    const nextLog = log.append({
      kind: "resolver.failed",
      data: {
        kind: "StealCard",
        reason: "rng-pick-out-of-bounds",
        pickIdx,
        eligibleCount: eligibleInstances.length,
      } as JsonValue,
    });
    return { result: EffectResult.Failed, log: nextLog, services };
  }

  // Transfer: change ownerSeat to drawer (zone stays Hand).
  const { newState: afterTransfer, log: transferLog } = transferOwner(
    services.cis,
    log,
    chosenInst.instanceId,
    ctx.sourceSeat,
  );

  const nextLog = transferLog.append({
    kind: "resolver.applied",
    data: {
      kind: "StealCard",
      sourceSeat: ctx.sourceSeat,
      victimSeat,
      instanceId: chosenInst.instanceId,
      cardAssetId: chosenInst.cardAssetId,
      pickIdx,
      eligibleCount: eligibleInstances.length,
      typeFilter,
    } as JsonValue,
  });

  return {
    result: EffectResult.Applied,
    log: nextLog,
    services: { ...services, cis: afterTransfer },
  };
}

// ---------------------------------------------------------------------------
// GiveCard — R19 GC
//
// Transfers one card from the DRAWER's hand to the chosen opponent's hand.
// Inverse of StealCard — used as the give-arm of Policy_Exchange SWAP.
//
// Target allowlist: OpponentChosen only (same picker as StealCard).
// Hand-full guard: if opponent's hand is at MAX_HAND_SIZE, the give is FORFEIT
// (Skipped, no transfer) — mirrors revealAuction HIGH-2 forfeit pattern.
// Drawer MUST have ≥ 1 card in hand for the give to fire; otherwise Skipped.
// The specific card to give is chosen by the store layer (pendingGiveCard state)
// BEFORE this resolver runs — ctx.giveCardInstanceId carries it.
// When ctx.giveCardInstanceId is absent / undefined, the resolver picks the
// drawer's first non-self card as a fallback (deterministic).
// ---------------------------------------------------------------------------
function resolveGiveCard(
  e: CardEffect,
  ctx: Readonly<EffectContext>,
  services: PipelineServices,
  log: ActionLog,
): ResolverResult {
  // Target allowlist: OpponentChosen only.
  if (e.target !== "OpponentChosen") {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "GiveCard",
        reason: "unsupported-target",
        target: e.target,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  if (ctx.victimSeat < 0) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "GiveCard",
        reason: "victim-seat-not-set",
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }
  const recipientSeat = ctx.victimSeat;

  // Prevent giving to oneself.
  if (recipientSeat === ctx.sourceSeat) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "GiveCard",
        reason: "self-give-blocked",
        sourceSeat: ctx.sourceSeat,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Hand-full guard for recipient: if opponent at MAX_HAND_SIZE, forfeit.
  const recipientHandCount = getCISByOwner(services.cis, recipientSeat).filter(
    (inst) => inst.zone === CardZone.Hand,
  ).length;
  if (recipientHandCount >= MAX_HAND_SIZE) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "GiveCard",
        reason: "recipient-hand-full",
        recipientSeat,
        recipientHandCount,
        MAX_HAND_SIZE,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Gather drawer's Hand-zone instances (excluding the played card itself if known).
  const drawerInstances = getCISByOwner(services.cis, ctx.sourceSeat).filter(
    (inst) => inst.zone === CardZone.Hand,
  );
  if (drawerInstances.length === 0) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "GiveCard",
        reason: "drawer-hand-empty",
        drawerSeat: ctx.sourceSeat,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Use first card in hand as deterministic fallback (store layer pre-selects via
  // pendingGiveCard; pipeline resolver is the fallback path only).
  const chosenInst = drawerInstances[0];
  if (chosenInst === undefined) {
    return { result: EffectResult.Skipped, log, services };
  }

  // Transfer: change ownerSeat to recipient (zone stays Hand).
  const { newState: afterTransfer, log: transferLog } = transferOwner(
    services.cis,
    log,
    chosenInst.instanceId,
    recipientSeat,
  );

  const nextLog = transferLog.append({
    kind: "resolver.applied",
    data: {
      kind: "GiveCard",
      sourceSeat: ctx.sourceSeat,
      recipientSeat,
      instanceId: chosenInst.instanceId,
      cardAssetId: chosenInst.cardAssetId,
    } as JsonValue,
  });

  return {
    result: EffectResult.Applied,
    log: nextLog,
    services: { ...services, cis: afterTransfer },
  };
}

// ---------------------------------------------------------------------------
// CancelDisaster — WEB.4.C
//
// Source text: "CANCEL ONE NATURAL DISASTER CARD"
// Implementation: wrapper that registers a single-charge Immunity entry
// protecting the drawer against destruction-class effect kinds
// (Destroy, Decommission, RngBranch, DiceOutcome) — the set of effect kinds
// associated with Crisis/disaster cards.
//
// Design rationale: Web rules engine does not track "Crisis card" explicitly
// as a source category at interception time. The practical effect is that the
// immunity intercepts the NEXT destruction-class effect aimed at the drawer,
// regardless of whether the source card was a Crisis card or not.
// This is the closest faithful representation of "CANCEL ONE NATURAL DISASTER".
//
// Target: Self/Drawer only (protection is self-applied).
// charges = 1 (single use — consumed on first interception).
// ---------------------------------------------------------------------------
function resolveCancelDisaster(
  e: CardEffect,
  ctx: Readonly<EffectContext>,
  services: PipelineServices,
  log: ActionLog,
): ResolverResult {
  // Target allowlist: Self / Drawer only.
  let ownerSeat: number;
  switch (e.target) {
    case "Self":
    case "Drawer":
      ownerSeat = ctx.sourceSeat;
      break;
    default: {
      const nextLog = log.append({
        kind: "resolver.skipped",
        data: {
          kind: "CancelDisaster",
          reason: "unsupported-target",
          target: e.target,
        } as JsonValue,
      });
      return { result: EffectResult.Skipped, log: nextLog, services };
    }
  }

  // Destruction-class kinds that disaster cards use.
  // CancelDisaster intercepts the next one aimed at the drawer.
  const disasterKinds: CardEffectKind[] = ["Destroy", "Decommission", "RngBranch", "DiceOutcome"];

  const instanceId = ctx.cardInstanceId > 0 ? ctx.cardInstanceId : 0;
  const sourceCardName =
    ctx.cardTags.length > 0
      ? (ctx.cardTags[0] ?? "Disaster_Preparedness_Fun")
      : "Disaster_Preparedness_Fun";

  const instance: ImmunityInstance = {
    instanceId,
    ownerSeat,
    tileIndex: null, // seat-level; matches any disaster targeting this seat
    againstKinds: disasterKinds,
    sourceCardName,
    chargesRemaining: 1,
    isPassive: false, // consumed on first use
  };

  let regResult: ReturnType<typeof registerImmunity>;
  try {
    regResult = registerImmunity(services.immunity, log, instance);
  } catch (err) {
    const nextLog = log.append({
      kind: "resolver.failed",
      data: {
        kind: "CancelDisaster",
        reason: String(err instanceof Error ? err.message : err),
      } as JsonValue,
    });
    return { result: EffectResult.Failed, log: nextLog, services };
  }

  const nextLog = regResult.log.append({
    kind: "resolver.applied",
    data: {
      kind: "CancelDisaster",
      instanceId,
      ownerSeat,
      disasterKinds,
      charges: 1,
      sourceCardName,
    } as JsonValue,
  });

  return {
    result: EffectResult.Applied,
    log: nextLog,
    services: { ...services, immunity: regResult.newState },
  };
}

// ---------------------------------------------------------------------------
// FacilityTransfer — WEB.4.C
//
// Source text: "BUY ANY FACILITY FROM THE MOST POLLUTING PLAYER FOR HALF THE COST"
// Implementation: drawer pays Math.ceil(facility.buildCostPaid * 0.5) tokens
// to the facility owner, then ownership transfers to drawer.
//
// Caller must supply:
//   ctx.victimSeat = current owner of the facility
//   ctx.targetTileIndices[0] = tile index of the facility to buy
//
// Validation:
//   • tile must have a facility (else Skipped)
//   • facility.ownerSeat must match ctx.victimSeat (else Skipped)
//   • drawer must have enough tokens to pay (else Skipped)
//
// Cost formula: Math.ceil(buildCostPaid * 0.5)
//   — mirrors Decommission refund formula (locked rule: ceil rounding).
//   — cost paid to victim (token transfer: victim gains, drawer loses).
//
// Target allowlist: OpponentChosen (buyer picks the seller + tile).
// ---------------------------------------------------------------------------
function resolveFacilityTransfer(
  e: CardEffect,
  ctx: Readonly<EffectContext>,
  services: PipelineServices,
  log: ActionLog,
): ResolverResult {
  // Fail-closed target allowlist.
  let victimSeat: number;
  switch (e.target) {
    case "OpponentChosen":
    case "OpponentRandom":
    case "NearestPlayer":
      if (ctx.victimSeat < 0) {
        const nextLog = log.append({
          kind: "resolver.skipped",
          data: {
            kind: "FacilityTransfer",
            reason: "victim-seat-not-set",
            target: e.target,
          } as JsonValue,
        });
        return { result: EffectResult.Skipped, log: nextLog, services };
      }
      victimSeat = ctx.victimSeat;
      break;
    default: {
      const nextLog = log.append({
        kind: "resolver.skipped",
        data: {
          kind: "FacilityTransfer",
          reason: "unsupported-target",
          target: e.target,
        } as JsonValue,
      });
      return { result: EffectResult.Skipped, log: nextLog, services };
    }
  }

  // Tile must be specified.
  const tileIndex = ctx.targetTileIndices[0];
  if (tileIndex === undefined) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: { kind: "FacilityTransfer", reason: "no-tile-specified" } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Facility must exist.
  const facility = getByTile(services.facilityOwnership, tileIndex);
  if (facility === null) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: { kind: "FacilityTransfer", reason: "tile-empty", tileIndex } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Facility owner must match victimSeat.
  if (facility.ownerSeat !== victimSeat) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "FacilityTransfer",
        reason: "wrong-owner",
        tileIndex,
        facilityOwner: facility.ownerSeat,
        victimSeat,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Half-price cost (ceil rounding, mirrors Decommission refund formula).
  const cost = Math.ceil(facility.buildCostPaid * 0.5);
  const drawerSeat = ctx.sourceSeat;
  const srcLabel = `FacilityTransfer:Tile${tileIndex}`;

  // Drawer must afford the cost.
  const {
    ok,
    newState: tokenAfterSpend,
    log: spendLog,
  } = trySpend(services.token, log, drawerSeat, cost, srcLabel);

  if (!ok) {
    const nextLog = spendLog.append({
      kind: "resolver.skipped",
      data: {
        kind: "FacilityTransfer",
        reason: "insufficient-tokens",
        drawerSeat,
        cost,
        balance: getTokenBalance(services.token, drawerSeat),
        tileIndex,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Pay the cost to the victim (seller receives the tokens).
  const { newState: tokenAfterPay, log: payLog } = addTokens(
    tokenAfterSpend,
    spendLog,
    victimSeat,
    cost,
    srcLabel,
  );

  // Transfer facility ownership.
  let transferResult: ReturnType<typeof transferFacility>;
  try {
    transferResult = transferFacility(services.facilityOwnership, payLog, tileIndex, drawerSeat);
  } catch (err) {
    const nextLog = payLog.append({
      kind: "resolver.failed",
      data: {
        kind: "FacilityTransfer",
        reason: err instanceof FacilityOwnershipError ? err.code : String(err),
        tileIndex,
      } as JsonValue,
    });
    return { result: EffectResult.Failed, log: nextLog, services };
  }

  const nextLog = transferResult.log.append({
    kind: "resolver.applied",
    data: {
      kind: "FacilityTransfer",
      drawerSeat,
      victimSeat,
      tileIndex,
      facilityType: facility.facilityType,
      buildCostPaid: facility.buildCostPaid,
      cost,
      target: e.target,
    } as JsonValue,
  });

  return {
    result: EffectResult.Applied,
    log: nextLog,
    services: {
      ...services,
      token: tokenAfterPay,
      facilityOwnership: transferResult.newState,
    },
  };
}

// ---------------------------------------------------------------------------
// DrawCard — WEB.4.C
//
// Source text examples:
//   Paris Climate Agreement: "DRAW ONE MITIGATION CARD FOR FREE"
//   Perovskite Solar Breakthrough: "DRAW A FREE MITIGATION CARD" (conditional arm)
//
// Implementation: draws the top card from the appropriate deck pool, mints a
// CIS CardInstance in the drawer's Hand zone, returns updated deck + cis.
//
// Pool selection:
//   cardPoolFilter: ["Mitigate"] → draw top Mitigate-type card from Policy pool
//                                   (scanning draw pile, skipping non-matching).
//   cardPoolFilter: ["Event"]    → draw from Event pool (any type).
//   absent / []                  → draw top card from Policy pool (any type).
//
// Note on Mitigate pool: Mitigate-type cards live in the Policy pool (deck bucketing
// rule: Policy/Tech/Mitigate all → Policy pool). DrawCard scans the Policy pool's
// draw pile for the first card matching the type filter.
//
// Hand-full (MAX_HAND_SIZE = 5): returns Skipped with reason "drawer-hand-full".
// Pool exhausted: returns Skipped with reason "pool-exhausted".
// Deck not supplied: returns Skipped with reason "deck-not-supplied".
//
// Target: Self / Drawer only (draws always go to the drawer).
// ---------------------------------------------------------------------------
function resolveDrawCard(
  e: CardEffect,
  ctx: Readonly<EffectContext>,
  services: PipelineServices,
  log: ActionLog,
): ResolverResult {
  // Target allowlist: Self / Drawer only.
  switch (e.target) {
    case "Self":
    case "Drawer":
      break; // drawer = ctx.sourceSeat always
    default: {
      const nextLog = log.append({
        kind: "resolver.skipped",
        data: {
          kind: "DrawCard",
          reason: "unsupported-target",
          target: e.target,
        } as JsonValue,
      });
      return { result: EffectResult.Skipped, log: nextLog, services };
    }
  }

  const drawerSeat = ctx.sourceSeat;

  // Deck must be supplied (optional field on PipelineServices).
  if (services.deck === undefined) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: { kind: "DrawCard", reason: "deck-not-supplied" } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Hand-full guard.
  const drawerHandCount = getCISByOwner(services.cis, drawerSeat).filter(
    (inst) => inst.zone === CardZone.Hand,
  ).length;

  if (drawerHandCount >= MAX_HAND_SIZE) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "DrawCard",
        reason: "drawer-hand-full",
        drawerSeat,
        drawerHandCount,
        MAX_HAND_SIZE,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Determine pool key and optional card-type filter.
  // cardPoolFilter[0]: "Event" → Event pool; anything else → Policy pool.
  const cpf = e.cardPoolFilter;
  const poolTypeFilter: string | null =
    cpf !== undefined && cpf.length > 0 ? (cpf[0] ?? null) : null;
  const poolKey = poolTypeFilter === "Event" ? "Event" : "Policy";

  // For a typed filter within Policy pool (e.g. ["Mitigate"]), scan the draw
  // pile for the first matching card. If found, remove it and reshuffle rest.
  let deckState = services.deck;
  let drawnCard: CardDef | null = null;

  if (poolTypeFilter !== null && poolTypeFilter !== "Event") {
    // Scan draw pile for first matching card type.
    const pool = deckState.pools[poolKey];
    const matchIdx = pool.draw.findIndex((c) => c.cardType === poolTypeFilter);

    if (matchIdx === -1) {
      // No matching card in draw pile; also check discard.
      const discardMatchIdx = pool.discard.findIndex((c) => c.cardType === poolTypeFilter);
      if (discardMatchIdx === -1) {
        const nextLog = log.append({
          kind: "resolver.skipped",
          data: {
            kind: "DrawCard",
            reason: "no-matching-card-in-pool",
            poolKey,
            poolTypeFilter,
          } as JsonValue,
        });
        return { result: EffectResult.Skipped, log: nextLog, services };
      }
      // Found in discard — reshuffle then retry.
      const reshuffledDraw = services.rng.shuffle([...pool.draw, ...pool.discard]);
      const reshuffledPool = {
        draw: Object.freeze(reshuffledDraw),
        discard: Object.freeze([]),
      };
      deckState = Object.freeze({
        pools: Object.freeze({ ...deckState.pools, [poolKey]: Object.freeze(reshuffledPool) }),
      });
      const newMatchIdx = deckState.pools[poolKey].draw.findIndex(
        (c) => c.cardType === poolTypeFilter,
      );
      if (newMatchIdx === -1) {
        const nextLog = log.append({
          kind: "resolver.skipped",
          data: {
            kind: "DrawCard",
            reason: "no-matching-card-after-reshuffle",
            poolKey,
            poolTypeFilter,
          } as JsonValue,
        });
        return { result: EffectResult.Skipped, log: nextLog, services };
      }
      drawnCard = deckState.pools[poolKey].draw[newMatchIdx] ?? null;
      const newDraw = [
        ...deckState.pools[poolKey].draw.slice(0, newMatchIdx),
        ...deckState.pools[poolKey].draw.slice(newMatchIdx + 1),
      ];
      deckState = Object.freeze({
        pools: Object.freeze({
          ...deckState.pools,
          [poolKey]: Object.freeze({ draw: Object.freeze(newDraw), discard: Object.freeze([]) }),
        }),
      });
    } else {
      drawnCard = pool.draw[matchIdx] ?? null;
      const newDraw = [...pool.draw.slice(0, matchIdx), ...pool.draw.slice(matchIdx + 1)];
      deckState = Object.freeze({
        pools: Object.freeze({
          ...deckState.pools,
          [poolKey]: Object.freeze({
            draw: Object.freeze(newDraw),
            discard: Object.freeze(pool.discard),
          }),
        }),
      });
    }
  } else {
    // No type filter — draw top card from pool (with reshuffle if empty).
    try {
      const drawResult = drawFromPool(deckState, poolKey, services.rng);
      deckState = drawResult.newState;
      drawnCard = drawResult.card;
    } catch {
      const nextLog = log.append({
        kind: "resolver.skipped",
        data: {
          kind: "DrawCard",
          reason: "pool-exhausted",
          poolKey,
        } as JsonValue,
      });
      return { result: EffectResult.Skipped, log: nextLog, services };
    }
  }

  if (drawnCard === null) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: { kind: "DrawCard", reason: "drawn-card-null" } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Mint CIS instance in drawer's Hand zone.
  const {
    newState: cisAfterCreate,
    instanceId: newInstId,
    log: cisLog,
  } = createForSeat(services.cis, log, drawnCard.name, drawerSeat, CardZone.Hand);

  // Register aura-passive if applicable (Whenever cards auto-register on acquisition).
  const { newAura: auraAfter, log: auraLog } = tryRegisterAuraPassive(
    services.aura,
    cisLog,
    drawnCard,
    drawerSeat,
    newInstId,
  );

  const nextLog = auraLog.append({
    kind: "resolver.applied",
    data: {
      kind: "DrawCard",
      drawerSeat,
      cardName: drawnCard.name,
      cardType: drawnCard.cardType,
      newInstanceId: newInstId,
      poolKey,
      poolTypeFilter,
    } as JsonValue,
  });

  return {
    result: EffectResult.Applied,
    log: nextLog,
    services: {
      ...services,
      cis: cisAfterCreate,
      aura: auraAfter,
      deck: deckState,
    },
  };
}

// ---------------------------------------------------------------------------
// WEB.4.D — DiscardHand resolver
//
// Source text: Military_Coup "DISCARD ALL POLICY CARD"
//
// Iterates all seats (0–3), finds every CardInstance in the Hand zone whose
// card type matches the filter, and moves each to the Discard zone.
//
// card type lookup: loadCards() returns the full card catalog; build a
// Map<cardAssetId, cardType> to classify each CIS instance. This call is
// intentionally inside the resolver (pure function — no module-level cache)
// so no singleton state is introduced. loadCards() caches the JSON parse
// inside card-deck-manager.ts so the cost is a Map build (cheap).
//
// cardPoolFilter: if present, only discard cards of matching type(s).
//                 absent → discard ALL types from Hand (no current card uses this).
//
// Target allowlist: Global only (affects all players). Other targets → Skipped.
// ---------------------------------------------------------------------------

function resolveDiscardHand(
  e: CardEffect,
  _ctx: Readonly<EffectContext>,
  services: PipelineServices,
  log: ActionLog,
): ResolverResult {
  // Fail-closed target allowlist — DiscardHand is a Global effect by definition.
  if (e.target !== "Global") {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "DiscardHand",
        reason: "unsupported-target",
        target: e.target,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Build cardType lookup from authoritative card catalog.
  const catalog = loadCards();
  const typeByName = new Map<string, string>();
  for (const card of catalog.cards) {
    typeByName.set(card.name, card.cardType);
  }

  // Collect type filter from cardPoolFilter (e.g. ["Policy"]).
  const typeFilter: ReadonlySet<string> =
    e.cardPoolFilter !== undefined && e.cardPoolFilter.length > 0
      ? new Set(e.cardPoolFilter)
      : new Set(); // empty = no filter (discard all types)

  let currentCis = services.cis;
  let currentLog = log;
  let discardCount = 0;

  // Iterate all seats 0–3 (hot-seat game: 4 players max).
  for (let seat = 0; seat <= 3; seat++) {
    const handInstances = getCISByOwner(currentCis, seat).filter(
      (inst) => inst.zone === CardZone.Hand,
    );

    for (const inst of handInstances) {
      // Type-filter check.
      if (typeFilter.size > 0) {
        const cardType = typeByName.get(inst.cardAssetId);
        if (cardType === undefined || !typeFilter.has(cardType)) continue;
      }

      // Move to Discard zone.
      try {
        const moveResult = moveZone(currentCis, currentLog, inst.instanceId, CardZone.Discard);
        currentCis = moveResult.newState;
        currentLog = moveResult.log;
        discardCount++;
      } catch {
        // Card not found in CIS (data inconsistency) — log and continue.
        currentLog = currentLog.append({
          kind: "resolver.failed",
          data: {
            kind: "DiscardHand",
            reason: "cis-move-failed",
            instanceId: inst.instanceId,
            cardAssetId: inst.cardAssetId,
            seat,
          } as JsonValue,
        });
      }
    }
  }

  const nextLog = currentLog.append({
    kind: "resolver.applied",
    data: {
      kind: "DiscardHand",
      discardCount,
      typeFilter: typeFilter.size > 0 ? [...typeFilter] : "all",
    } as JsonValue,
  });

  return {
    result: discardCount > 0 ? EffectResult.Applied : EffectResult.Skipped,
    log: nextLog,
    services: { ...services, cis: currentCis },
  };
}

// ---------------------------------------------------------------------------
// resolveTrigger — WEB.4.E: register a passive reactive trigger
//
// Called when a card has an effect with kind="Trigger". Registers a
// TriggerInstance into services.trigger state so that future applicable
// events fire the response effects.
//
// Schema fields consumed:
//   triggerOn:       TriggerEvent — which event fires this trigger.
//   triggerResponse: CardEffect[] — effects to run when trigger fires.
//   valueInt:        chargesRemaining (absent = TRIGGER_PERMANENT_CHARGES).
//                    Use TRIGGER_PERMANENT_CHARGES (999) for permanent triggers
//                    that fire every time the event occurs.
//
// Target allowlist: "Self" / "Drawer" only (triggers are always self-registered).
// Fail conditions:
//   • target not Self/Drawer → Skipped (no-op).
//   • triggerOn absent → Skipped.
//   • triggerResponse absent / empty → Skipped.
//   • services.trigger absent → Skipped (trigger state not wired).
//   • cardInstanceId not set → Failed (required for dedupe on discard).
// ---------------------------------------------------------------------------

function resolveTrigger(
  e: CardEffect,
  ctx: Readonly<EffectContext>,
  services: PipelineServices,
  log: ActionLog,
): ResolverResult {
  // Self/Drawer only — triggers are always registered on the drawer's seat.
  if (e.target !== "Self" && e.target !== "Drawer") {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: {
        kind: "Trigger",
        reason: "unsupported-target",
        target: e.target,
      } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // triggerOn required.
  if (e.triggerOn === undefined) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: { kind: "Trigger", reason: "triggerOn-missing" } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // triggerResponse required and non-empty.
  if (e.triggerResponse === undefined || e.triggerResponse.length === 0) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: { kind: "Trigger", reason: "triggerResponse-empty" } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  // Trigger state must be wired.
  if (services.trigger === undefined) {
    const nextLog = log.append({
      kind: "resolver.skipped",
      data: { kind: "Trigger", reason: "trigger-state-not-wired" } as JsonValue,
    });
    return { result: EffectResult.Skipped, log: nextLog, services };
  }

  const chargesRemaining = e.valueInt !== undefined ? e.valueInt : TRIGGER_PERMANENT_CHARGES;

  // instanceId is auto-assigned by registerTrigger from TriggerState.nextId.
  // sourceCardName uses cardInstanceId for log attribution (may be 0 for test-only).
  const input: TriggerInstanceInput = {
    ownerSeat: ctx.sourceSeat,
    triggerOn: e.triggerOn,
    response: Object.freeze([...e.triggerResponse]),
    chargesRemaining,
    sourceCardName: `cis:${ctx.cardInstanceId}`,
  };

  let registerResult: ReturnType<typeof registerTrigger>;
  try {
    registerResult = registerTrigger(services.trigger, log, input);
  } catch (err) {
    console.error(`[Resolver:Trigger] registerTrigger threw: ${String(err)}`);
    const nextLog = log.append({
      kind: "resolver.failed",
      data: { kind: "Trigger", reason: "register-threw" } as JsonValue,
    });
    return { result: EffectResult.Failed, log: nextLog, services };
  }

  const nextLog = registerResult.log.append({
    kind: "resolver.applied",
    data: {
      kind: "Trigger",
      sourceSeat: ctx.sourceSeat,
      triggerOn: e.triggerOn,
      chargesRemaining,
      responseEffectCount: e.triggerResponse.length,
    } as JsonValue,
  });

  return {
    result: EffectResult.Applied,
    log: nextLog,
    services: { ...services, trigger: registerResult.newState },
  };
}

// ---------------------------------------------------------------------------
// resolve — main dispatch. Returns null when no resolver registered (unknown kind).
// ---------------------------------------------------------------------------

export function resolve(
  e: CardEffect,
  ctx: Readonly<EffectContext>,
  services: PipelineServices,
  log: ActionLog,
  scaledDelta: number,
): ResolverResult | null {
  switch (e.kind) {
    case "TokenDelta":
      return resolveTokenDelta(e, ctx, services, log, scaledDelta);
    case "PollutionDelta":
      return resolvePollutionDelta(e, ctx, services, log, scaledDelta);
    case "EnergyDelta":
      return resolveEnergyDelta(e, ctx, services, log, scaledDelta);
    case "StealEnergy":
      return resolveStealEnergy(e, ctx, services, log);
    case "AuraApply":
      return resolveAuraApply(e, ctx, services, log);
    case "BuildCostMod":
      return resolveBuildCostMod(e, ctx, services, log);

    // WEB.3.A: Cluster F — Destruction resolvers (promoted from deferred)
    case "Decommission":
      return resolveDecommission(e, ctx, services, log);
    case "Destroy":
      return resolveDestroy(e, ctx, services, log);
    case "StealFacility":
      return resolveStealFacility(e, ctx, services, log);

    // WEB.3.B: Cluster G — DiceOutcome promoted from deferred stub
    case "DiceOutcome":
      return resolveDiceOutcome(e, ctx, services, log);

    // WEB.3.C: Cluster L — RngBranch promoted from deferred stub
    case "RngBranch":
      return resolveRngBranch(e, ctx, services, log);

    // WEB.4.B: Immunity promoted from deferred stub
    case "Immunity":
      return resolveImmunity(e, ctx, services, log);

    // WEB.4.C: promoted from deferred stubs
    case "StealCard":
      return resolveStealCard(e, ctx, services, log);
    // R19 GC: GiveCard — inverse of StealCard for Policy_Exchange SWAP arm
    case "GiveCard":
      return resolveGiveCard(e, ctx, services, log);
    case "CancelDisaster":
      return resolveCancelDisaster(e, ctx, services, log);
    case "FacilityTransfer":
      return resolveFacilityTransfer(e, ctx, services, log);
    case "DrawCard":
      return resolveDrawCard(e, ctx, services, log);

    // WEB.4.D: DiscardHand promoted from new resolver
    case "DiscardHand":
      return resolveDiscardHand(e, ctx, services, log);

    // WEB.4.E: Trigger — register passive reactive trigger
    case "Trigger":
      return resolveTrigger(e, ctx, services, log);

    // Conditional: NOT registered — gated upstream in EffectPipeline.
    // TollMod: not a separate resolver — flows through AuraApply with auraScope=Toll.
    default:
      return null;
  }
}
